import React, { useEffect, useState, useMemo } from "react";

import initials from "initials";

import * as S from "./Avatar.styles";
import { Tooltip } from "../Tooltip";

export type Size = "small" | "large" | "extraSmall";

export type Props = {
  userImage?: string;
  userName: string;
  alt?: string;
  size: Size;
  className?: string;
  withToolTip?: boolean;
};

// only exported to be used in the story
export const tertiaryColors = [
  "#FFCDD2",
  "#F8BBD0",
  "#E1BEE7",
  "#C9D0E4",
  "#C5CAE9",
  "#BBDEFB",
  "#B3E5FC",
  "#B2EBF2",
  "#B2DFDB",
  "#D7CCC8",
  "#E5DAD5",
  "#FFCCBC",
  "#FFE0B2",
  "#FFECB3",
  "#FFF9C4",
  "#F0F4C3",
  "#DCEDC8",
  "#C8E6C9"
];

const imageExist = (url: string) =>
  new Promise<boolean>(resolve => {
    const img = new Image();
    img.src = url;
    img.onerror = () => resolve(false);
    img.onload = () => resolve(true);
  });

const Avatar = ({
  userImage,
  userName,
  alt,
  size,
  withToolTip,
  ...props
}: Props) => {
  const [image, setImage] = useState<string>();
  const slicedUserName = (userName ?? "")
    .replace(/-/g, "")
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2);

  // If username has no spaces, fallback to using the first two characters
  if (slicedUserName.length === 0 && userName?.length > 1) {
    slicedUserName.push(userName[0], userName[1] || "");
  }

  const userInitials =
    slicedUserName.length > 1
      ? initials(slicedUserName.join(" "))
      : initials(userName?.slice(0, 2) ?? "");

  const index =
    slicedUserName
      // Fallback to 0 for empty strings in staging
      .map(char => char.charCodeAt(0) || 0)
      .reduce((sum, code) => sum + code, 0) % tertiaryColors.length;
  const backgroundColor = tertiaryColors[index];

  const clipName = useMemo(() => {
    if (!userName) return "N/A";

    return (
      userName
        .split(/[\s.]+/)
        .slice(0, 2)
        .join(" ") || userName.slice(0, 2)
    );
  }, [userName]);

  useEffect(() => {
    if (userImage) {
      imageExist(userImage).then(result => {
        if (result) {
          setImage(userImage);
        }
      });
    }
  }, [userImage]);

  const baseAvatar = image ? (
    <S.Image src={image} alt={alt} />
  ) : (
    <S.Fallback $backgroundColor={backgroundColor}>
      {userInitials.toUpperCase()}
    </S.Fallback>
  );

  let avatarEl = baseAvatar;

  if (withToolTip) {
    avatarEl = (
      <Tooltip tooltip={clipName} align="center">
        {baseAvatar}
      </Tooltip>
    );
  }

  return (
    <S.Root {...props} size={size} data-pendo-redact-text>
      {avatarEl}
    </S.Root>
  );
};

export default Avatar;
